import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { fetchClassement, fetchMatchs } from '../../api/fff';

const equipeOptions = [
  'SENIORS N2', 'SENIORS R1', 'U18 R1', 'U17 R2', 'U16 R1', 'U15 R1', 'U14 Ligue',
  'U18F R2', 'U16 D2', 'U14 D1', 'U13 Niveau A', 'U12 Niveau A', 'U11 Niveau A', 'U10 Niveau A', 'Autre'
];
const saisonOptions = ['2023/2024', '2024/2025'];
const defaultForm = {
  equipe: '',
  saison: saisonOptions[0],
  competition: '',
  classement: '',
  points: '',
  victoires: '',
  nuls: '',
  defaites: '',
  buts_marques: '',
  buts_encaisses: '',
  forme: ['', '', '', '', ''],
  evolution_classement: ['', '', '', '', '', '', '', '', '', ''],
  top_buteurs: [{ nom: '', buts: '' }],
  top_passeurs: [{ nom: '', passes: '' }],
};

const Stats = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [stats, setStats] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // Charger les stats existantes
  useEffect(() => {
    const q = query(collection(db, 'stats_equipes'), orderBy('equipe'), orderBy('saison', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setStats(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Ajout/suppression top buteurs/passeurs
  const handleTopChange = (type, idx, field, value) => {
    const arr = [...form[type]];
    arr[idx][field] = value;
    setForm({ ...form, [type]: arr });
  };
  const addTop = (type) => setForm({ ...form, [type]: [...form[type], { nom: '', [type === 'top_buteurs' ? 'buts' : 'passes']: '' }] });
  const removeTop = (type, idx) => setForm({ ...form, [type]: form[type].filter((_, i) => i !== idx) });

  // Forme du moment (V/N/D)
  const handleFormeChange = (idx, value) => {
    const arr = [...form.forme];
    arr[idx] = value;
    setForm({ ...form, forme: arr });
  };

  // Évolution classement
  const handleEvolChange = (idx, value) => {
    const arr = [...form.evolution_classement];
    arr[idx] = value;
    setForm({ ...form, evolution_classement: arr });
  };

  // Sauvegarde (add/edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback('');
    try {
      const data = {
        ...form,
        classement: Number(form.classement) || 0,
        points: Number(form.points) || 0,
        victoires: Number(form.victoires) || 0,
        nuls: Number(form.nuls) || 0,
        defaites: Number(form.defaites) || 0,
        buts_marques: Number(form.buts_marques) || 0,
        buts_encaisses: Number(form.buts_encaisses) || 0,
        forme: form.forme,
        evolution_classement: form.evolution_classement,
        top_buteurs: form.top_buteurs,
        top_passeurs: form.top_passeurs,
        updatedAt: new Date(),
      };
      if (editId) {
        await updateDoc(doc(db, 'stats_equipes', editId), data);
        setFeedback('Statistiques modifiées !');
      } else {
        await addDoc(collection(db, 'stats_equipes'), {
          ...data,
          createdAt: new Date(),
        });
        setFeedback('Statistiques ajoutées !');
      }
      setForm(defaultForm);
      setEditId(null);
    } catch (err) {
      setFeedback('Erreur : ' + err.message);
    }
    setLoading(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  // Édition
  const handleEdit = (stat) => {
    setForm({
      equipe: stat.equipe || '',
      saison: stat.saison || saisonOptions[0],
      competition: stat.competition || '',
      classement: stat.classement ?? '',
      points: stat.points ?? '',
      victoires: stat.victoires ?? '',
      nuls: stat.nuls ?? '',
      defaites: stat.defaites ?? '',
      buts_marques: stat.buts_marques ?? '',
      buts_encaisses: stat.buts_encaisses ?? '',
      forme: stat.forme || ['', '', '', '', ''],
      evolution_classement: stat.evolution_classement || ['', '', '', '', '', '', '', '', '', ''],
      top_buteurs: stat.top_buteurs || [{ nom: '', buts: '' }],
      top_passeurs: stat.top_passeurs || [{ nom: '', passes: '' }],
    });
    setEditId(stat.id);
  };

  // Suppression
  const handleDelete = async () => {
    if (!toDelete) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'stats_equipes', toDelete.id));
      setFeedback('Statistiques supprimées.');
    } catch (err) {
      setFeedback('Erreur lors de la suppression : ' + err.message);
    }
    setShowConfirm(false);
    setToDelete(null);
    setLoading(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  // Import automatique depuis l'API FFF
  const handleImportFFF = async () => {
    setFeedback('Import en cours...');
    try {
      // 1. Classement (trouver l'équipe sélectionnée)
      const classement = await fetchClassement();
      const eq = classement.find(t => {
        // On matche sur le nom d'équipe (à adapter si besoin)
        return t.equipe?.nom?.toLowerCase().includes(form.equipe.toLowerCase());
      });
      // 2. Matchs (pour victoires/nuls/défaites/buts/forme)
      const { past } = await fetchMatchs();
      // On filtre les matchs de l'équipe sélectionnée
      const matchsEquipe = past.filter(m => m.home?.nom?.toLowerCase().includes(form.equipe.toLowerCase()) || m.away?.nom?.toLowerCase().includes(form.equipe.toLowerCase()));
      // Calculs
      let victoires = 0, nuls = 0, defaites = 0, buts_marques = 0, buts_encaisses = 0;
      let forme = [];
      matchsEquipe.forEach(match => {
        let isHome = match.home?.nom?.toLowerCase().includes(form.equipe.toLowerCase());
        let scoreHome = match.home_score, scoreAway = match.away_score;
        if (scoreHome == null || scoreAway == null) return;
        let marque = isHome ? scoreHome : scoreAway;
        let encaisse = isHome ? scoreAway : scoreHome;
        buts_marques += marque;
        buts_encaisses += encaisse;
        if (marque > encaisse) victoires++;
        else if (marque === encaisse) nuls++;
        else defaites++;
        // Forme du moment (V/N/D)
        if (forme.length < 5) {
          if (marque > encaisse) forme.push('V');
          else if (marque === encaisse) forme.push('N');
          else forme.push('D');
        }
      });
      // Remplir les champs si l'API fournit la donnée
      setForm(f => ({
        ...f,
        classement: eq?.classement || f.classement,
        points: eq?.points || f.points,
        competition: eq?.competition?.nom || f.competition,
        victoires: victoires || f.victoires,
        nuls: nuls || f.nuls,
        defaites: defaites || f.defaites,
        buts_marques: buts_marques || f.buts_marques,
        buts_encaisses: buts_encaisses || f.buts_encaisses,
        forme: forme.length === 5 ? forme : f.forme,
      }));
      setFeedback('Import API FFF terminé !');
    } catch (err) {
      setFeedback('Erreur import API FFF : ' + err.message);
    }
    setTimeout(() => setFeedback(''), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gestion des Statistiques d'équipe</h1>
      {/* Tableau des stats existantes */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-2">Stats enregistrées</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-[#00238D] rounded-xl shadow-lg">
            <thead>
              <tr className="text-[#FFE400] text-left">
                <th className="py-2 px-4">Équipe</th>
                <th className="py-2 px-4">Saison</th>
                <th className="py-2 px-4">Compétition</th>
                <th className="py-2 px-4">Classement</th>
                <th className="py-2 px-4">Points</th>
                <th className="py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-white py-8">Aucune stat enregistrée.</td></tr>
              ) : (
                stats.map(stat => (
                  <tr key={stat.id} className="border-b border-[#001B56] hover:bg-[#001B56]/40 transition">
                    <td className="py-2 px-4 font-bold">{stat.equipe}</td>
                    <td className="py-2 px-4">{stat.saison}</td>
                    <td className="py-2 px-4">{stat.competition}</td>
                    <td className="py-2 px-4">{stat.classement}</td>
                    <td className="py-2 px-4">{stat.points}</td>
                    <td className="py-2 px-4 flex gap-2">
                      <button className="p-2 rounded hover:bg-[#FFE400]/20 transition" title="Éditer" onClick={() => handleEdit(stat)}>✏️</button>
                      <button className="p-2 rounded hover:bg-red-500/30 transition" title="Supprimer" onClick={() => { setShowConfirm(true); setToDelete(stat); }}>🗑️</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Formulaire d'ajout/édition */}
      <form className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-3xl flex flex-col gap-6 animate-fade-in" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">Équipe</label>
            <select name="equipe" value={form.equipe} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
              <option value="">Choisir...</option>
              {equipeOptions.map(eq => <option key={eq} value={eq}>{eq}</option>)}
            </select>
          </div>
          <div>
            <label className="block mb-1">Saison</label>
            <select name="saison" value={form.saison} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
              {saisonOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block mb-1">Compétition</label>
            <input type="text" name="competition" value={form.competition} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
          <div>
            <label className="block mb-1">Classement</label>
            <input type="number" name="classement" value={form.classement} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
          <div>
            <label className="block mb-1">Points</label>
            <input type="number" name="points" value={form.points} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block mb-1">Victoires</label>
            <input type="number" name="victoires" value={form.victoires} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
          <div>
            <label className="block mb-1">Nuls</label>
            <input type="number" name="nuls" value={form.nuls} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
          <div>
            <label className="block mb-1">Défaites</label>
            <input type="number" name="defaites" value={form.defaites} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">Buts marqués</label>
            <input type="number" name="buts_marques" value={form.buts_marques} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
          <div>
            <label className="block mb-1">Buts encaissés</label>
            <input type="number" name="buts_encaisses" value={form.buts_encaisses} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
          </div>
        </div>
        {/* Forme du moment */}
        <div>
          <label className="block mb-1">Forme du moment (5 derniers matchs)</label>
          <div className="flex gap-2">
            {form.forme.map((r, i) => (
              <select key={i} value={r} onChange={e => handleFormeChange(i, e.target.value)} className="w-16 px-2 py-1 rounded bg-[#001B56] text-white">
                <option value="">-</option>
                <option value="V">V</option>
                <option value="N">N</option>
                <option value="D">D</option>
              </select>
            ))}
          </div>
        </div>
        {/* Évolution du classement */}
        <div>
          <label className="block mb-1">Évolution du classement (par journée)</label>
          <div className="flex gap-2 flex-wrap">
            {form.evolution_classement.map((v, i) => (
              <input key={i} type="number" value={v} onChange={e => handleEvolChange(i, e.target.value)} className="w-14 px-2 py-1 rounded bg-[#001B56] text-white" placeholder={`J${i+1}`} />
            ))}
          </div>
        </div>
        {/* Top buteurs */}
        <div>
          <label className="block mb-1">Top buteurs</label>
          {form.top_buteurs.map((b, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <input type="text" value={b.nom} onChange={e => handleTopChange('top_buteurs', i, 'nom', e.target.value)} placeholder="Nom" className="px-2 py-1 rounded bg-[#001B56] text-white" />
              <input type="number" value={b.buts} onChange={e => handleTopChange('top_buteurs', i, 'buts', e.target.value)} placeholder="Buts" className="w-20 px-2 py-1 rounded bg-[#001B56] text-white" />
              <button type="button" onClick={() => removeTop('top_buteurs', i)} className="text-red-400 font-bold">×</button>
            </div>
          ))}
          <button type="button" onClick={() => addTop('top_buteurs')} className="text-[#BDFF00] font-bold">+ Ajouter</button>
        </div>
        {/* Top passeurs */}
        <div>
          <label className="block mb-1">Top passeurs</label>
          {form.top_passeurs.map((p, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <input type="text" value={p.nom} onChange={e => handleTopChange('top_passeurs', i, 'nom', e.target.value)} placeholder="Nom" className="px-2 py-1 rounded bg-[#001B56] text-white" />
              <input type="number" value={p.passes} onChange={e => handleTopChange('top_passeurs', i, 'passes', e.target.value)} placeholder="Passes" className="w-20 px-2 py-1 rounded bg-[#001B56] text-white" />
              <button type="button" onClick={() => removeTop('top_passeurs', i)} className="text-red-400 font-bold">×</button>
            </div>
          ))}
          <button type="button" onClick={() => addTop('top_passeurs')} className="text-[#BDFF00] font-bold">+ Ajouter</button>
        </div>
        {/* Coupe de France */}
        <div className="border-t border-[#FFE400]/30 pt-4 mt-4">
          <label className="block mb-1 font-bold text-[#FFE400]">Coupe de France</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1">Tour atteint</label>
              <select name="cdf_tour" value={form.cdf_tour || ''} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
                <option value="">Choisir…</option>
                <option value="128ème">128ème de finale</option>
                <option value="64ème">64ème de finale</option>
                <option value="32ème">32ème de finale</option>
                <option value="16ème">16ème de finale</option>
                <option value="8ème">8ème de finale</option>
                <option value="Quart">Quart de finale</option>
                <option value="Demi">Demi-finale</option>
                <option value="Finale">Finale</option>
                <option value="Vainqueur">Vainqueur</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Dernier adversaire</label>
              <input type="text" name="cdf_adversaire" value={form.cdf_adversaire || ''} onChange={handleChange} placeholder="Nom du club" className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
            </div>
            <div>
              <label className="block mb-1">Date du match</label>
              <input type="date" name="cdf_date" value={form.cdf_date || ''} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
            </div>
          </div>
        </div>
        {/* Bouton import API FFF */}
        <button type="button" onClick={handleImportFFF} className="bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold px-4 py-2 rounded-xl shadow transition w-fit">Importer depuis l'API FFF</button>
        {/* Bouton sauvegarde (à brancher plus tard) */}
        <button type="submit" className="bg-[#BDFF00] hover:bg-[#FFE400] text-[#001B56] font-bold px-6 py-3 rounded-xl shadow transition w-fit self-end" disabled={loading}>{editId ? 'Modifier' : 'Sauvegarder'}</button>
        {feedback && <div className="text-white font-semibold mt-2">{feedback}</div>}
      </form>
      {/* Confirmation suppression */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 animate-fade-in">
            <h2 className="text-xl font-bold">Confirmer la suppression</h2>
            <p>Voulez-vous vraiment supprimer ces statistiques ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowConfirm(false); setToDelete(null); }}>Annuler</button>
              <button className="px-6 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stats; 